import React, { useEffect, useContext, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Tasks from "./Tasks";
import { useNavigate, useLocation } from "react-router-dom";
import { SocketContext } from "../../context/socket";
import ChatBox from "./ChatBox";
import { getUserFromLocalStorage } from "../../utils/localStorage";
import api from "../../utils/axios";
import { chatLinkFor, taskIdFromSearch } from "../../utils/chatLink";
import { enablePushNotifications } from "../../utils/pushNotifications";
import { toast } from "react-toastify";
import { API_URL, LANGUAGE } from "../../constants/constants";
import { addUnread, newMessage, addOthersUnread } from "../../redux/taskSlice";
import { addMessage, updateEditMessage } from "../../redux/messageSlice";
import {
  addAssign,
  addConfirm,
  addEstimate,
  addTask,
  addChangeProgress,
  addComplete,
  addPay,
  setTasks,
  addOtherRead,
  addChangeOffer,
  deleteTask,
  removeUnread,
  submitRateTask,
  deleteAllTasks,
} from "../../redux/taskSlice";
import { deleteCurrentTask, setCurrentTask } from "../../redux/currentSlice";
import { changeBalance } from "../../redux/loginSlice";
import "../../assets/css/Chat.css";
import { useTranslation } from "react-i18next";

let selectedTask = {};
let msg = "wp-sofa.chat ";

export default function Chat() {
  const { t, i18n } = useTranslation();
  const currentTask = useSelector((state) => state.current);
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const task = useSelector((state) => state.current);
  const tasks = useSelector((state) => state.task);
  const [language, setLanguage] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Ask once, on the first authenticated screen the reader reaches.
  //
  // A notification from the page cannot appear on a phone at all, so this
  // registers the service worker and subscribes the browser instead. Everything
  // it can refuse to do is a reason, not an exception: an iPhone that has not
  // been added to the home screen, a reader who said no, a browser too old.
  // None of them is worth interrupting anybody over, so they are logged.
  useEffect(() => {
    let cancelled = false;
    enablePushNotifications().then((result) => {
      if (cancelled || result.ok) return;
      console.log("push notifications are off:", result.reason);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // /chat?taskId=<id> opens that conversation.
  //
  // This app never read the address at all. It did not have to: a link in a
  // notification mail points at wp-sofa.chat, and a phone opening one used to
  // be given the desktop app, which does read it. Since a phone is handed this
  // app instead, every one of those mails would land on the task list.
  //
  // It watches the router's location rather than reading window.location once,
  // so a notification clicked while this is already on screen works too.
  useEffect(() => {
    const taskId = taskIdFromSearch(location.search);
    if (!taskId) return;
    api
      .get(`${API_URL}/api/v1/task/single/${taskId}`)
      .then((res) => {
        dispatch(setCurrentTask(res.data));
        socket.emit("join-chat", res.data._id);
        navigate("/chat", { replace: true });
      })
      .catch(() => toast.error("This task could not be opened."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const languageList = [
    {
      language: t("english"),
      slanguage: "EN",
      value: LANGUAGE.EN,
      code: "en",
    },
    {
      language: t("german"),
      slanguage: "DE",
      value: LANGUAGE.DE,
      code: "de",
    },
    {
      language: "Spanish",
      slanguage: "ES",
      value: LANGUAGE.ES,
      code: "es",
    },
  ];

  useEffect(() => {
    const user = getUserFromLocalStorage("user");
    if (!user) {
      navigate("/");
    } else {
      // This is useful when user move from billing to chat
      // dispatch(login(user));
      socket.emit("setup", user);

      if (typeof user.language !== "undefined") setLanguage(user.language);
      else setLanguage(0);
    }
  }, []);

  useEffect(() => {
    let code;
    if (language === "en") {
      let user = getUserFromLocalStorage();
      user.language = LANGUAGE.EN;
      setLanguage(LANGUAGE.EN);
      code = languageList[LANGUAGE.EN].code;
    } else code = languageList[language].code;
    i18n.changeLanguage(code);
  }, [language]);

  useEffect(() => {
    selectedTask = task;
  }, [task]);

  useEffect(() => {
    tasks.forEach((item) => {
      if (item._id == task._id) dispatch(setCurrentTask(item));
    });
  }, [tasks]);

  // Socket init for message receive
  useEffect(() => {
    socket.on("message-received", async (newMessageReceived) => {
      console.log(`Message Received : ${newMessageReceived.task._id}`);

      const user = getUserFromLocalStorage();
      if (
        newMessageReceived.sender._id != user._id &&
        (newMessageReceived.task._id != selectedTask._id || !selectedTask._id)
      ) {
        if (newMessageReceived.message) {
          const avatar = newMessageReceived.sender.avatar;
          // react-desktop-notification opened window.open(url, <random name>),
          // so every click opened a brand new window, and the url it was given
          // was "chat": relative, and carrying no task, so it arrived at the
          // task list rather than the conversation the notification was about.
          // Neither thing it was meant to do happened.
          // On a phone this constructor is not allowed to run at all: Chrome on
          // Android throws "Illegal constructor" and requires a service worker
          // instead, and iOS only delivers notifications to an installed web
          // app. The library this replaces called the same constructor, so
          // notifications have never worked on a phone. It matters now in a way
          // it did not before, because a phone is handed this app rather than
          // the desktop one, so the throw would land in a socket handler and
          // take the message with it.
          try {
            if (
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              const notification = new Notification(
                `${newMessageReceived.sender.username} - ${newMessageReceived.task.taskname}`,
                {
                  body: newMessageReceived.message,
                  icon: `${API_URL}/avatar/${avatar}`,
                },
              );
              notification.onclick = () => {
                window.focus();
                notification.close();
                navigate(chatLinkFor(newMessageReceived.task._id));
              };
            } else if (
              "Notification" in window &&
              Notification.permission !== "denied"
            ) {
              // The library asked on the first message that arrived. Keep that,
              // or a reader who has not answered the prompt never gets asked.
              Notification.requestPermission();
            }
          } catch (error) {
            // A phone. The toast below still tells them.
          }
        }
        if (newMessageReceived.message) {
          // msg = newMessageReceived.message + "   ";

          msg = `There is a new message! Task: ${newMessageReceived.task.taskname}  `;
          toast.success(`${newMessageReceived.message}`, { theme: "colored" });
        } else if (newMessageReceived.images.length > 0)
          toast.success("Image sent");
        else toast.success("File sent");
      }

      // await api
      //   .post(`${API_URL}/api/v1/task/latestMessage`, {
      //     taskId: newMessageReceived.task._id,
      //     latestMessage: newMessageReceived._id,
      //   })
      //   .then((res) => {
      dispatch(
        newMessage({
          taskId: newMessageReceived.task._id,
          message: newMessageReceived.message,
        }),
      );
      // })
      // .catch((err) => {
      //   if (err.response.status == 401) {
      //     navigate("/");
      //   }
      //   const errors = err.response.data.errors;
      //   errors.forEach((error) => toast.error(error.msg));
      // });

      const taskId = newMessageReceived.task._id;
      const userId = user._id;
      const senderId = newMessageReceived.sender._id;
      dispatch(addOthersUnread({ taskId, userId, senderId }));

      if (
        !selectedTask._id ||
        selectedTask._id != newMessageReceived.task._id
      ) {
        // await api
        //   .post(`${API_URL}/api/v1/task/unread`, {
        //     taskId: newMessageReceived.task._id,
        //   })
        //   .then((res) => {
        //     dispatch(addUnread({ task: newMessageReceived.task, user: user }));
        //   })
        //   .catch((err) => {
        //     if (err.response.status == 401) {
        //       navigate("/");
        //     }
        //     const errors = err.response.data.errors;
        //     errors.forEach((error) => toast.error(error.msg));
        //   });
        dispatch(addUnread({ task: newMessageReceived.task, user: user }));
      } else {
        // setMessages((messages) => [...messages, newMessageReceived]);
        dispatch(addMessage(newMessageReceived));
        socket.emit("message-read", {
          task: newMessageReceived.task,
          userId: user._id,
        });
      }
    });

    socket.on("message-edited", async (editMessageReceived) => {
      if (editMessageReceived.task._id == selectedTask._id)
        dispatch(
          updateEditMessage({
            id: editMessageReceived._id,
            message: editMessageReceived.message,
          }),
        );
    });

    return () => {
      socket.removeListener("message-received");
      socket.removeListener("message-edited");
    };
  }, []);

  // Socket init for task realted part
  useEffect(() => {
    socket.on("task-received", (newTaskReceived) => {
      const user = getUserFromLocalStorage();
      console.log(`Task Received: ${newTaskReceived}`);

      toast.info(`Task «${newTaskReceived.taskname}» is created`, {
        theme: "colored",
      });

      dispatch(addTask(newTaskReceived));
      // if (taskLength == 0 || newTaskReceived.client == user._id) {
      //   dispatch(setCurrentTask(newTaskReceived));
      // }
      socket.emit("join-chat", newTaskReceived._id);
    });

    socket.on("task-deleted", (taskDeleteReceived) => {
      console.log(`Task Received: ${taskDeleteReceived}`);

      toast.info(`Task «${taskDeleteReceived.taskname}» is deleted`, {
        theme: "colored",
      });

      if (selectedTask._id == taskDeleteReceived._id)
        dispatch(deleteCurrentTask());
      dispatch(deleteTask(taskDeleteReceived._id));
    });

    socket.on("estimate-received", (newEstimateReceived) => {
      console.log(`Estimate : ${newEstimateReceived}`);

      toast.info(`Task «${newEstimateReceived.taskname}» is estimated`, {
        theme: "colored",
      });

      dispatch(addEstimate(newEstimateReceived));
    });

    socket.on("changeoffer-received", (newChangeOfferReceived) => {
      console.log(`Change Offer : ${newChangeOfferReceived}`);

      toast.info(`Task «${newChangeOfferReceived.taskname}» offer is changed`, {
        theme: "colored",
      });

      dispatch(addChangeOffer(newChangeOfferReceived));
    });

    socket.on("confirm-received", (newConfirmReceived) => {
      console.log(`Confirm : ${newConfirmReceived}`);

      toast.info(`Task «${newConfirmReceived.taskname}» is confirmed`, {
        theme: "colored",
      });

      dispatch(addConfirm(newConfirmReceived));
    });

    socket.on("changeprogress-received", (newChangeProgressReceived) => {
      console.log(`Progress Changed : ${newChangeProgressReceived}`);

      toast.info(
        `Task «${newChangeProgressReceived.taskname}» is done by ${newChangeProgressReceived.percent}%`,
        {
          theme: "colored",
        },
      );

      dispatch(addChangeProgress(newChangeProgressReceived));
    });

    socket.on("assign-received", (newAssignReceived) => {
      const user = getUserFromLocalStorage();
      console.log(`Assigned : ${newAssignReceived}`);

      toast.info(`Task «${newAssignReceived.taskname}» is assigned`, {
        theme: "colored",
      });

      dispatch(addAssign({ task: newAssignReceived, role: user.role }));
    });

    socket.on("complete-received", (newCompleteReceived) => {
      console.log(`Completed : ${newCompleteReceived}`);

      toast.info(`Task «${newCompleteReceived.taskname}» is completed`, {
        theme: "colored",
      });

      dispatch(addComplete(newCompleteReceived));
    });

    socket.on("pay-received", (newPayReceived) => {
      console.log(`Paid : ${newPayReceived}`);

      const { cost } = newPayReceived;
      dispatch(changeBalance(cost));

      toast.info(`Task «${newPayReceived.taskname}» is paid`, {
        theme: "colored",
      });

      dispatch(addPay(newPayReceived));
    });

    socket.on("submitrate-received", (newSubmitRateReceived) => {
      console.log(`Submit Rate : ${newSubmitRateReceived}`);

      const { cost } = newSubmitRateReceived;
      toast.info(`Task «${newSubmitRateReceived.taskname}» is get rate`, {
        theme: "colored",
      });

      dispatch(submitRateTask(newSubmitRateReceived));
    });

    socket.on("read-received", (data) => {
      const { taskId, userId } = data;
      const user = getUserFromLocalStorage();
      if (userId == user._id)
        dispatch(removeUnread({ taskId: taskId, userId }));
      else dispatch(addOtherRead({ taskId: taskId, userId: userId }));
    });

    return () => {
      socket.removeListener("task-received");
      socket.removeListener("task-deleted");
      socket.removeListener("estimate-received");
      socket.removeListener("changeoffer-received");
      socket.removeListener("confirm-received");
      socket.removeListener("changeprogress-received");
      socket.removeListener("assign-received");
      socket.removeListener("complete-received");
      socket.removeListener("pay-received");
      socket.removeListener("read-received");
      socket.removeListener("submitrate-received");
    };
  }, []);

  return (
    <div>
      {currentTask._id ? <ChatBox /> : <Tasks />}

      {/* <div className="col-8 height-100">
        {currentTask._id ? <ChatBox /> : <ChatBoxBackground />}
      </div> */}
    </div>
  );
}
